/**
 * 模組頁面代碼 : 1210-UI-04
 * 頁面說明 : 樓層設定列表畫面
 * @module web/modules/elevator/components/FloorListModal
 */


// ----- import from npm modules ----
import $ from 'jquery';
import React, { useState, useEffect, useRef } from 'react';

// ----- import from local modules -----
import {
    AccessModeEnums,
    ModuleEnums,
} from '../../../components/BaseComponent';
import {
    DataTableComponent,
} from '../../../components/CommonComponent';
import { MessageModalModeEnums } from 'web/components/MessageModal';

// ----- constants -----
const RESPONSE_OK = 200;
const TITLE_FLOOR_SETTING = '樓層設定';


const FloorListModal = React.forwardRef((props, ref) => {
    const { checkPermission, elevator, floorList, needFloorListLoad, setNeedFloorListLoad } = props;
 
    // 放置樓層資料更新陣列物件
    const [updateFloorList, setUpdateFloorList] = useState([]);
    const floorListRef = useRef(floorList);

    // 繪製樓層列表表格
    const prepareFloorListTable = () => {
        // 定義資料內容
        const dataSet = floorList.map((floor) => {
            return [
                floor.floorNum,
                floor.floorDisplay,
                floor.flgStatus
            ];
        });

        // 定義欄位屬性
        const columnConfig = [
            { title: '實際樓層', className: 'dt-center', data: 0 },
            {
                orderable: false,
                title: '顯示樓層',
                className: 'dt-center',
                data: 1,
                render: (data, type, row, meta) => {
                    let inputFloorId = 'input-floor-' + meta.row;
                    const disabledInput = checkPermission(ModuleEnums.FLOOR, AccessModeEnums.UPDATE) ? '' : 'disabled';
                    return `<input class="form-control" id="${inputFloorId}" name="${inputFloorId}" type="text" aria-label="" value="${data}" ${disabledInput} />`;
                }
            },
            {
                orderable: false,
                order: null,
                title: '啟用',
                data: 2,
                className: 'dt-center',
                render: (data, type, row, meta) => {
                    let enableFloorId = 'eab-floor-' + meta.row;
                    let isChecked = data === 'A' ? 'checked' : '';
                    let enableButton = checkPermission(ModuleEnums.FLOOR, AccessModeEnums.DELETE) ? '' : 'disabled';
                    return `<div class="form-check form-switch">
                                <input class="form-check-input" type="checkbox" role="switch" id="${enableFloorId}" name="${enableFloorId}" ${isChecked} ${enableButton} />
                                <label class="form-check-label" for="${enableFloorId}"></label>
                            </div>`;
                },
            }
        ];

        // 定義排序設定
        const orderConfig = {
            idx: 0,
            dir: 'dsc',
        };

        // 定義固定欄位設定
        const fixedColumnsConfig = {
            start: 0,
        };

        // 先清除datatable
        if ($.fn.DataTable.isDataTable('#datatable__floorList')) {
            $('#datatable__floorList').DataTable().destroy();
            $('#datatable__floorList').empty();
        }
    
        return DataTableComponent('#datatable__floorList', dataSet, columnConfig, orderConfig, fixedColumnsConfig, false, true);
    };

    const handleChange = (event) => {
        const target = event.target;
        const rowIndex = target.closest('tr').rowIndex;
        const floor = floorListRef.current[rowIndex - 1];

        const inputFloorId = `input-floor-${rowIndex - 1}`;
        const enableFloorId = `eab-floor-${rowIndex - 1}`;
        const floorDisplay = target.closest('tr').querySelector(`input[name="${inputFloorId}"]`).value;
        const floorCheck = target.closest('tr').querySelector(`input[name="${enableFloorId}"]`).checked ? 'A' : 'I';

        if (floor.floorDisplay !== floorDisplay || floor.flgStatus !== floorCheck) {
            const updatedFloor = { ...floor, floorDisplay: floorDisplay, flgStatus: floorCheck };
            setUpdateFloorList(updateFloorList => {
                const existingIndex = updateFloorList.findIndex(f => f.rowIndex === rowIndex - 1);
                if (existingIndex === -1) {
                    return [...updateFloorList, { ...updatedFloor, rowIndex: rowIndex - 1, updated: true }];
                } else {
                    const newUpdateFloorList = [...updateFloorList];
                    newUpdateFloorList[existingIndex] = { ...updatedFloor, rowIndex: rowIndex - 1, updated: true };
                    return newUpdateFloorList;
                }
            });
        }
    };

    // 當表格的容器可見後，調整列寬
    const adjustTableWhenVisible = () => {
        const tableContainer = document.querySelector('#datatable__floorList');
        if (tableContainer && tableContainer.offsetParent !== null) {
            let t = prepareFloorListTable();
            t.columns.adjust().draw();

            // 地下樓層加區隔線
            const underFloorIndex = elevator.aboveFloors;
            const underFloorRow = tableContainer.rows[underFloorIndex];
            if (underFloorRow) {
                underFloorRow.classList.add('datatable__row--under-floor');
            }

            // 綁定輸入框變更事件
            $('#datatable__floorList').on('change', 'input[type="text"]', handleChange);
            // 綁定啟用欄位變更事件
            $('#datatable__floorList').on('change', 'input[type="checkbox"]', handleChange);
        } else {
            setTimeout(adjustTableWhenVisible, 100); // 容器不可見，稍後再嘗試
        }
    };

    useEffect(() => {
        floorListRef.current = floorList;
    }, [floorList]);

    useEffect(() => {
        if (needFloorListLoad) {
            setNeedFloorListLoad(false);

            const maskElement = document.querySelector('#modal-mask');
            // 顯示遮罩
            maskElement.style.opacity = '0.5';

            // 重新繪製表格
            adjustTableWhenVisible();

            // 隱藏遮罩
            maskElement.style.opacity = '0';
            setTimeout(() => {
                maskElement.style.display = 'none';
            }, 200);

            // 讀取樓層陣列到UpdateFloorList, 並加上rowIndex
            const updatedList = floorList.map((floor, index) => {
                return { ...floor, rowIndex: index };
            });
            setUpdateFloorList(updatedList);
       }
    }, [needFloorListLoad, floorList]);

    const handleSubmit = (e) => {
        e.preventDefault();
        // 檢查updateFloorList內的樓層實際名稱是否有重複
        const floorDisplaySet = new Set();
        for (let i = 0; i < updateFloorList.length; i++) {
            const floor = updateFloorList[i];
            if (floorDisplaySet.has(floor.floorDisplay)) {
                props.onShowMessageModal(MessageModalModeEnums.ERROR, TITLE_FLOOR_SETTING, '樓層顯示名稱重複');
                return;
            }
            floorDisplaySet.add(floor.floorDisplay);
        }

        // Handle form submission logic here
        updateFloorList.forEach((floor) => {
            if (floor.updated) {
                props.saveFloor(floor).then((response) => {
                    const statusCode = response.payload.code;
                    if (statusCode !== RESPONSE_OK) {
                        props.onShowMessageModal(MessageModalModeEnums.ERROR, TITLE_FLOOR_SETTING, response.payload);
                        return;
                    }
                });
            }
        });
        props.onShowMessageModal(MessageModalModeEnums.INFO, TITLE_FLOOR_SETTING, '樓層設定已儲存');
        // Clear updateFloorList
        setUpdateFloorList([]);
    };

    return (
        <div ref={ref}
            className="modal fade"
            id="modalFloorList"
            data-bs-backdrop="static"
            data-bs-keyboard="false"
            tabIndex="-1"
            aria-labelledby="modalFloorListLabel"
            aria-hidden="true"
        >
            <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
                <form className="modal__form modal-content needs-validation" id="modalFloorForm" onSubmit={handleSubmit}>
                    <div className="modal-header">
                        <h1 className="modal-title fs-5" id="modalFloorLabel">
                            <div className="modal__title__badge">
                                <svg className="badge__icon" aria-hidden="true">
                                    <use href="#ico-building"></use>
                                </svg>
                            </div>
                            <div className="modal__title__text">[{elevator?.elevatorNum}]樓層設定</div>
                        </h1>
                        <button type="button" className="btn-close btn-cancel" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div className="modal-body">
                        <div id="modal-mask" className="modal-mask"></div>
                        <div className="datatable__data">
                            <table id="datatable__floorList" className="display table table-hover align-middle nowrap" style={{ width: '100%' }}></table>
                        </div>
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-cancel" data-bs-dismiss="modal">取消</button>
                        <button type="submit" className="btn btn-primary">儲存</button>
                    </div>
                </form>
            </div>
        </div>
    );
});

export default FloorListModal;